import { useEffect, useState } from "react";
import { Users, CreditCard, Store, DollarSign, Ticket, Clock, MapPin, MessageSquare, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { BusinessAccounts } from "@/components/BusinessAccounts";
import { AppReviewsAdmin } from "@/components/AppReviewsAdmin";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Card({ icon: Icon, label, value, sub, tid, accent = "#FF3B6B" }) {
  return (
    <div className="rounded-2xl bg-[#151C2C] border border-[#26334D] p-5">
      <Icon className="h-5 w-5" style={{ color: accent }} />
      <p data-testid={tid} className="text-3xl font-extrabold mt-2">{value}</p>
      <p className="text-sm text-slate-300">{label}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function OwnerDashboard() {
  const [s, setS] = useState(null);
  const load = () => api.get("/admin/stats").then((r) => setS(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);
  if (!s) return <main className="min-h-[60vh]" />;

  return (
    <main data-testid="owner-dashboard" className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <p className="text-xs uppercase tracking-[0.2em] text-[#FF3B6B]">Owner</p>
      <h1 className="text-4xl font-extrabold tracking-tight mt-1">Your business at a glance</h1>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card icon={DollarSign} accent="#00E5A3" tid="owner-mrr" label="Monthly recurring revenue" value={money(s.mrr)} sub={`${s.paying_travelers} × $9.99 + ${s.paying_families} × $19.99 + ${s.paying_vendors} × $25`} />
        <Card icon={TrendingUp} accent="#00E5A3" tid="owner-revenue-month" label="Collected this month" value={money(s.revenue_this_month)} sub={`All time: ${money(s.revenue_all_time)}`} />
        <Card icon={Users} accent="#60A5FA" tid="owner-signups" label="New sign-ups (30 days)" value={s.signups_30d} />
      </div>

      <h2 className="text-base md:text-lg font-bold mt-10 mb-4">Travelers</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={Users} tid="owner-travelers" label="Total travelers" value={s.travelers} />
        <Card icon={CreditCard} tid="owner-paying-travelers" label="Paying ($9.99/mo)" value={s.paying_travelers} accent="#00E5A3" />
        <Card icon={Users} tid="owner-paying-families" label="Paying Family ($19.99/mo)" value={s.paying_families} accent="#00E5A3" />
        <Card icon={Clock} tid="owner-on-trial" label="On free trial" value={s.on_trial} sub={`${s.family_trials} on Family trial`} accent="#FFB800" />
        <Card icon={Ticket} tid="owner-promo" label="Promo code access" value={s.promo_users} accent="#C084FC" />
        <Card icon={Clock} tid="owner-expired" label="Trial ended, not paying" value={Math.max(0, s.expired_trials)} accent="#64748B" />
      </div>

      <h2 className="text-base md:text-lg font-bold mt-10 mb-4">Businesses</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={Store} tid="owner-vendors" label="Business accounts" value={s.vendors} accent="#FFB800" />
        <Card icon={CreditCard} tid="owner-paying-vendors" label="Paying ($25/mo)" value={s.paying_vendors} accent="#00E5A3" />
        <Card icon={Store} tid="owner-businesses" label="Listings created" value={s.businesses} accent="#FFB800" />
        <Card icon={Store} tid="owner-live-businesses" label="Listings live" value={s.live_businesses} accent="#00E5A3" />
      </div>
      <BusinessAccounts onChange={load} />
      <div className="mt-10"><AppReviewsAdmin /></div>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <section className="min-w-0 rounded-2xl bg-[#151C2C] border border-[#26334D] p-5">
          <h3 className="font-bold mb-3">Recent sign-ups</h3>
          {s.recent_signups.map((u) => (
            <div key={u.email} data-testid="owner-signup-row" className="flex justify-between gap-3 text-sm py-2 border-t border-[#26334D] first:border-0">
              <span className="truncate min-w-0">{u.name} <span className="text-slate-500">· {u.email}</span></span>
              <span className="text-xs text-slate-400 shrink-0">{u.role === "vendor" ? "Business" : "Traveler"} · {u.plan.replace("_monthly", "")}</span>
            </div>
          ))}
        </section>
        <section className="min-w-0 rounded-2xl bg-[#151C2C] border border-[#26334D] p-5">
          <h3 className="font-bold mb-3 flex items-center gap-2"><MapPin className="h-4 w-4 text-[#FF3B6B]" /> Recently searched cities <span className="text-xs text-slate-500 font-normal">({s.cities_searched} total)</span></h3>
          {s.top_cities.map((c) => (
            <div key={c.city} data-testid="owner-city-row" className="flex justify-between text-sm py-2 border-t border-[#26334D] first:border-0">
              <span>{c.city}</span><span className="text-xs text-slate-400">{c.listings} places</span>
            </div>
          ))}
          <p className="text-xs text-slate-500 mt-3 flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> {s.reviews} traveler reviews posted</p>
        </section>
      </div>
    </main>
  );
}
